/**
 * Single source of truth for mapping pro's `VITE_ENVIRONMENT` build var
 * to the Sentry `environment` tag.
 *
 * Extracted from `sentry-init.ts` so it stays trivially unit-testable
 * without pulling in the Sentry SDK.
 *
 * `VITE_ENVIRONMENT` is CI-injected (`react-admin-cicd.yml`) with one of
 * `development` | `test` | `staging` | `production` — the same
 * vocabulary Sentry expects, so this is mostly a pass-through. The
 * explicit allowlist + `unknown` fallback exist so an unset or typo'd
 * value can never silently resolve to a value indistinguishable from a
 * real environment. Deriving this from `window.location.hostname`
 * instead is the KB-2372 `resolveProjectId` failure class this ticket
 * exists to avoid — get the environment wrong and prod and staging
 * become indistinguishable in the Sentry UI, which is worse than no tag.
 */

export type SentryEnvironment = 'production' | 'staging' | 'test' | 'development' | 'unknown';

const KNOWN_ENVIRONMENTS: ReadonlySet<string> = new Set<string>([
  'production',
  'staging',
  'test',
  'development',
]);

export function resolveSentryEnvironment(viteEnvironment: string | undefined): SentryEnvironment {
  if (viteEnvironment && KNOWN_ENVIRONMENTS.has(viteEnvironment)) {
    return viteEnvironment as SentryEnvironment;
  }
  return 'unknown';
}
